import React, { useState, useEffect, useMemo } from "react";
import { BASE } from "../utils/api";
import { isWithinLast24h, last24hCutoff } from "../utils/timeWindow";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  IconButton,
  Alert,
  Stack,
  Chip,
  TablePagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Checkbox,
  FormControlLabel,
  SelectChangeEvent
} from "@mui/material";
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

interface SportsBet {
  id: number;
  username:string;
  user_id:number;
  match_title: string;
  team_one: string;
  team_two: string;
  game_type: string;
  selection_name: string;
  category: string;
  bet_type: string;
  odds: number;
  stake_amount: number;
  usd_amount: number;
  status: string;
  result_status : string;
  exposure_after_bet: number;
  match_start_time: string;
  match_end_time: string;
  created_at: string;
}

interface ApiResponse {
  success: boolean;
  bets: SportsBet[];
  pagination: {
    total: number;
    totalPages: number;
    page: number;
    limit: number;
  };
  error?: string;
}

interface Filters {
  status: string;
  gameResult: string;
  startDate: string;
  endDate: string;
  currency: string;
}

interface DeleteDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const Result_TYPES = [
  { value: "won", label: "Won" },
  { value: "loss", label: "Loss" },
  { value: "pending", label: "Pending" },

];

const STATUS_TYPES = [
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
  
];

/** Batch size pulled from the API so filtering/paging can happen client-side. */
const FETCH_LIMIT = 500;

const lightFilterFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    color: "#111827 !important",
  },
  "& .MuiOutlinedInput-input": {
    color: "#111827 !important",
  },
  "& .MuiSelect-select": {
    color: "#111827 !important",
  },
  "& .MuiSvgIcon-root": {
    color: "#111827",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#111827 !important",
    opacity: 1,
  },
  "& .MuiInputLabel-root": {
    color: "#6b7280 !important",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#25ee89 !important",
  },
  "& .MuiOutlinedInput-root fieldset": {
    borderColor: "#d1d5db",
  },
  "& .MuiOutlinedInput-root:hover fieldset": {
    borderColor: "#25ee89",
  },
  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
    borderColor: "#25ee89",
  },
  "& input[type='date']::-webkit-calendar-picker-indicator": {
    filter: "none",
    opacity: 0.8,
  },
};

const DeleteDialog = ({ open, onClose, onConfirm }: DeleteDialogProps) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Confirm Delete</DialogTitle>
    <DialogContent>
      Are you sure you want to delete this bet? This action cannot be undone.
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onConfirm} color="error" variant="contained">
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

const SportsBetting = () => {
  const [bets, setBets] = useState<SportsBet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBetId, setSelectedBetId] = useState<number | null>(null);
  const staffId  = localStorage.getItem('currentUserId');
  const [filters, setFilters] = useState<Filters>({
    status: "",
    gameResult: "",
    startDate: "",
    endDate: "",
    currency: "usd"
  });
  // Default view = only bets placed in the last 24 hours.
  const [last24h, setLast24h] = useState(true);

  // Picking a date range replaces the rolling 24h window.
  const pickDate = (key: "startDate" | "endDate", value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (value) setLast24h(false);
    setPage(0);
  };

  const toggle24h = (on: boolean) => {
    setLast24h(on);
    if (on) setFilters(prev => ({ ...prev, startDate: "", endDate: "" }));
    setPage(0);
  };

  // Helper to determine the timestamp to sort by for a bet
  const getBetTime = (bet: SportsBet): number => {
    // prefer match_start_time, then match_end_time, then fallback to id (so sorting remains deterministic)
    const tCandidates = [bet.match_start_time, bet.match_end_time];
    for (const t of tCandidates) {
      if (t) {
        const ts = Date.parse(t);
        if (!isNaN(ts)) return ts;
      }
    }
    // fallback to id (descending) by using id as a timestamp-like number
    return bet.id || 0;
  };

  const fetchBets = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch one large batch so the 24h window + pagination can be applied client-side.
      const searchParams = new URLSearchParams({
        page: "1",
        limit: FETCH_LIMIT.toString(),
        ...(filters.status && { status: filters.status }),
        ...(filters.gameResult && { gameResult: filters.gameResult }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(searchTerm && { search: searchTerm }),
        currency: filters.currency
      });

      const response = await fetch(
        `${BASE}/sportsbetting/admin/bets?${searchParams.toString()}`,
        {
          headers: {
            "x-staff-id": staffId ?? "",
          }
        }
      );
      const data: ApiResponse = await response.json();

      if (data.success) {
        // sort bets by time descending (newest first)
        const sorted = (data.bets || []).slice().sort((a, b) => {
          return getBetTime(b) - getBetTime(a);
        });
        setBets(sorted);
      } else {
        setError(data.error || "Failed to fetch bets");
        setBets([]);
      }
    } catch (err) {
      setError("Failed to fetch bets");
      setBets([]);
    } finally {
      setLoading(false);
    }
  };

  // Only server-side filters trigger a refetch; the 24h window is applied locally.
  useEffect(() => {
    fetchBets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.status,
    filters.gameResult,
    filters.startDate,
    filters.endDate,
    filters.currency,
    searchTerm
  ]);

  /* ---- client-side 24h window + pagination ---- */
  const visibleBets = useMemo(() => {
    if (!last24h) return bets;
    const cutoff = last24hCutoff();
    return bets.filter(b => isWithinLast24h(b.created_at, cutoff));
  }, [bets, last24h]);

  const totalCount = visibleBets.length;

  const pagedBets = useMemo(
    () => visibleBets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [visibleBets, page, rowsPerPage]
  );

  const handleDelete = async () => {
    if (selectedBetId) {
      try {
        const response = await fetch(
          `${BASE}/sportsbetting/admin/bets/${selectedBetId}`,
          { method: "DELETE", headers: { "x-staff-id": staffId ?? "" } }
        );
        const data = await response.json();
        if (data.success) {
          fetchBets();
          setDeleteDialogOpen(false);
        } else {
          setError(data.error || "Failed to delete bet");
        }
      } catch (err) {
        setError("Failed to delete bet");
      }
    }
  };

  const handleStatusUpdate = async (betId: number, newStatus: string) => {
    try {
      const response = await fetch(
        `${BASE}/sportsbetting/admin/bets/${betId}/status`,
        {
          method: "PUT",
          headers: { 
            "Content-Type": "application/json",
            "x-staff-id": staffId ?? ""
          },
          body: JSON.stringify({
            status: newStatus,
            resultDeclaredTime: new Date().toISOString()
          })
        }
      );
      const data = await response.json();
      if (data.success) {
        fetchBets();
      } else {
        setError(data.error || "Failed to update bet status");
      }
    } catch (err) {
      setError("Failed to update bet status");
    }
  };

  const handleStatusChange = (event: SelectChangeEvent<string>, betId: number) => {
    handleStatusUpdate(betId, event.target.value);
  };

  const getStatusColor = (status: string): "success" | "error" | "warning" => {
    switch (status.toLowerCase()) {
      case 'won':
        return 'success';
      case 'lost':
        return 'error';
      default:
        return 'warning';
    }
  };

  return (
    <Box sx={{ p: 3, ml: { xs: 0, lg: '256px' } }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Sports Betting Administration
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search bets..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              sx={lightFilterFieldSx}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  sx={lightFilterFieldSx}
                  value={filters.status}
                  label="Status"
                  onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(0); }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  {STATUS_TYPES.map(status => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Result Type</InputLabel>
                <Select
                  sx={lightFilterFieldSx}
                  value={filters.gameResult}
                  label="Result Type"
                  onChange={(e) => { setFilters({ ...filters, gameResult: e.target.value }); setPage(0); }}
                >
                  <MenuItem value="">All Result</MenuItem>
                  {Result_TYPES.map(game => (
                    <MenuItem key={game.value} value={game.value}>
                      {game.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                type="date"
                label="Start Date"
                value={filters.startDate}
                onChange={(e) => pickDate("startDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={lightFilterFieldSx}
              />
              
              <TextField
                type="date"
                label="End Date"
                value={filters.endDate}
                onChange={(e) => pickDate("endDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={lightFilterFieldSx}
              />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={last24h}
                    onChange={(e) => toggle24h(e.target.checked)}
                  />
                }
                label="Last 24 hours only"
              />
              <Typography variant="body2" color="textSecondary">
                {last24h
                  ? `Showing last 24 hours – ${totalCount} bet(s)`
                  : `Showing ${totalCount} bet(s)`}
              </Typography>
              {(filters.status || filters.gameResult || filters.startDate ||
                filters.endDate || searchTerm || !last24h) && (
                <Button
                  size="small"
                  onClick={() => {
                    setSearchTerm("");
                    setFilters({
                      status: "",
                      gameResult: "",
                      startDate: "",
                      endDate: "",
                      currency: "usd"
                    });
                    setLast24h(true);
                    setPage(0);
                  }}
                >
                  Clear filters
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: 0 } }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>User ID</TableCell>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>User Name</TableCell>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Match</TableCell>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Selection</TableCell>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Odds</TableCell>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }} align="right">Stake</TableCell>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Result</TableCell>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }} align="right">Exposure</TableCell>
                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : pagedBets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    {last24h ? "No bets in the last 24 hours" : "No bets found"}
                  </TableCell>
                </TableRow>
              ) : (
                pagedBets.map((bet) => {
                  // Calculate exposure: use exposure_after_bet if available, otherwise calculate
                  const exposureValue = Number(bet.exposure_after_bet || 0) !== 0
                    ? Number(bet.exposure_after_bet)
                    : bet.bet_type === 'lay'
                      ? bet.stake_amount * (bet.odds - 1)
                      : bet.stake_amount;

                  return (
                  <TableRow key={bet.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{bet.user_id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{bet.username}</Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Typography variant="subtitle2" sx={{ wordBreak: 'keep-all' }}>{bet.match_title}</Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ wordBreak: 'keep-all' }}>
                        {bet.team_one} vs {bet.team_two}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{bet.game_type}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {bet.category}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{bet.selection_name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {bet.bet_type}
                      </Typography>
                    </TableCell>
                    <TableCell>{bet.odds}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {bet.stake_amount.toFixed(2)} INR
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bet.status || 'open'}
                        size="small"
                        color={bet.status === 'closed' ? 'default' : 'info'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bet.result_status || 'pending'}
                        size="small"
                        color={getStatusColor(bet.result_status || 'pending')}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                        {exposureValue.toFixed(2)} INR
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">
                        {bet.created_at ? new Date(bet.created_at).toLocaleString() : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />

        <DeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDelete}
        />
      </Paper>
    </Box>
  );
};

export default SportsBetting;
